import { Select } from "@/components/ui/Select";
import { useDocumentTags } from "@/hooks/reports/useDocumentTags";

// LLM & Reports v3.2 §6.4 — single dropdown for picking a document tag
// on a per-attachment basis. Defaults to "Other" per the spec when no
// value is bound. The "Master" tag is filtered out of the user-facing
// list because it's auto-applied to the master document slot in
// Step 3 — letting users pick "Master" from a supporting-doc
// dropdown would create two masters on one submission.

export interface TagDropdownProps {
  value: string | null | undefined;
  onChange: (tagCode: string | null) => void;
  disabled?: boolean;
  /**
   * When true, includes the "Master" option. Use only on the master-
   * document row; supporting-doc rows leave this off.
   */
  allowMaster?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function TagDropdown({
  value,
  onChange,
  disabled,
  allowMaster = false,
  className,
  ariaLabel = "Document tag",
}: TagDropdownProps) {
  const { data, isLoading, isError } = useDocumentTags();

  const options = (data ?? []).filter(
    (t) => allowMaster || t.tagCode !== "Master",
  );

  return (
    <Select
      aria-label={ariaLabel}
      className={className}
      disabled={disabled || isLoading || isError}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">{isLoading ? "Loading tags…" : "Other"}</option>
      {options.map((tag) => (
        <option key={tag.id} value={tag.tagCode}>
          {tag.displayName}
        </option>
      ))}
    </Select>
  );
}
